import React, {useState, useEffect, useReducer} from 'react';

import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import _ from 'lodash';

import OutputText from '@ey/canvascoreservices/OutputText';
import Form from '@ey/canvascoreservices/Form';
import Input from '../../../../../CoreWrappers/Input';
import LoadingIndicator from '../../../../../Common/LoadingIndicator';
import ErrorSummary from '../../../../../Common/ErrorSummary/ErrorSummary';
import AddElementButton from '../../../../CreateProjectClassic/AddNewGroup/AddElementButton';

import {labels, currentResource} from '../../../../../../util/utils';
import {getProjectOwners} from '../../../../../../util/contactsDB';

import {updateEntities} from '../../../../../../actions/Project/projectAction';
import {raiseJSValidationError, clearErrors} from '../../../../../../actions/erroractions';
import {setIsModalOpen} from '../../../../../../actions/AccessGroups/SetIsModalOpen';

import AddNewDomain from './AddNewDomain/index';
import AddExistingDomain from './AddExistingDomain/index';
import StyledEditEntityForm from './StyledEditEntityForm';
import Modal from '../../../../../CoreWrappers/Modal';

const EditEntityForm = ({entity, project, showModal, hideModal}) => {
	const MAX_LENGTH = 100;
	const ENTITY_NAME = 'entityName';
	const DOMAIN_ID = 'domain_';
	const DOMAIN_LIST = 'domainList';

	// State for domains
	const [domainCount, setDomainCount] = useState(0);
	const [renderedDomains, setRenderedDomains] = useState([]); // Contains newly added empty domain object alse
	const [actualDomains, setActualDomains] = useState([]); // conatins only valid domain
	const [domainError, setDomainError] = useState(false);
	const [projectDomains, setProjectDomains] = useState([]);

	const [isLoading, setIsLoading] = useState(false);
	const projectOwners = getProjectOwners(project?.users ?? []);

	// editedEntity is sent through action In order to update the orgchart chunck.
	let editedEntity = {};

	const dispatch = useDispatch();

	const initialValues = {
		[ENTITY_NAME]: entity?.entityName,
		[DOMAIN_LIST]: []
	};

	const schema = {
		[ENTITY_NAME]: {required: true, maxLength: MAX_LENGTH},
		[DOMAIN_LIST]: {required: false}
	};

	const getUpdatedOrgStructure = (values, project, selectedDomains, entitiesToBeEdited) => {
		return project?.orgStructure?.map((node) => {
			let isEditedEntity = false;
			let editedEntityName = node.entityName;

			// Update Entity Name.
			if (node.entityId === entity.entityId) {
				// check that entities to edited are not duplicates.
				editedEntityName = values[ENTITY_NAME];
				entitiesToBeEdited.push(entity.id);
				isEditedEntity = true;
			}

			let newEntity = {
				id: node.id,
				parentId: node.parentId,
				entityId: node.entityId,
				entityName: editedEntityName,
				entityAdmins: node.admins,
				domains: isEditedEntity ? [...selectedDomains] : [...node.domains],
				contacts: node.contacts,
				accessGroups: [...node.accessGroups],
				engagements: [...node.engagements],
				engagementStatus: node.engagementStatus ?? ''
			};

			// editedEntity is sent through action In order to update the orgchart chunck.
			if (isEditedEntity) {
				editedEntity = {...newEntity};
			}

			return newEntity;
		});
	};

	const onSubmitHandler = (values, callback) => {
		setIsLoading(true);

		if (domainValidator()) {
			// Check that entities to be edited are unique
			const entitiesToBeEdited = [];
			const selectedDomains = renderedDomains.map((dom) => dom.label);
			const updatedOrgStructure = getUpdatedOrgStructure(values, project, selectedDomains, entitiesToBeEdited);

			const payload = {
				stepUpdated: 99,
				name: project.name,
				status: project.status,
				isSave: false,
				clients: [...project.clients],
				orgStructure: updatedOrgStructure,
				customColumns: [...project.customColumns],
				tags: [...project.tags],
				type: project.type,
				users: [...project.users],
				engagementCount: project.engagementCount,
				projectId: project.projectId,
				id: project.projectId,
				owners: [...projectOwners],
				entitiesToBeEdited
			};

			dispatch(updateEntities(project.projectId, payload, editedEntity))
				.then(() => {
					setIsLoading(false);
					dispatch(clearErrors());
					hideModal();
				})
				.catch((e) => {
					setIsLoading(false);
					hideModal();
					const errorCode = e?.response?.data?.errors?.Error[0]?.split('-')[0].trim() || '';
					if (currentResource.Errors[errorCode]) {
						dispatch(raiseJSValidationError(errorCode + ' - ' + currentResource.Errors[errorCode]));
					}
				});
		} else {
			setIsLoading(false);
			return;
		}
	};

	const formProps = {
		initialValues,
		schema,
		onSubmit: (values, callback) => onSubmitHandler(values, callback)
	};

	const renderEntityNameSection = ({values, errors, handleChange, ...rest}) => {
		const id = ENTITY_NAME;

		const inputProps = {
			id: id,
			value: values[id],
			label: labels.entityName,
			maxLength: MAX_LENGTH,
			errorMessage: errors[id] ? errors[id]?.message : null,
			onChange: (e) => handleChange(id, e.target.value)
		};

		return (
			<div className="entity-name-section">
				<Input {...inputProps} />
			</div>
		);
	};

	// ** Entity domain functions - Start **
	const resetStates = () => {
		setRenderedDomains([]);
		setActualDomains([]);
		setDomainError(false);
		dispatch(clearErrors()).then(() => {
			dispatch(setIsModalOpen(false));
		});
	};

	const buildDomainObjectList = (domainList) => {
		let counter = 0;
		let domainObjectList = [];

		domainList.forEach((domainName) => {
			counter = counter + 1;

			// Object structure merged. Same for both list.
			const domainObject = {
				id: `${DOMAIN_ID}${counter}`,
				label: domainName,
				isEmpty: false,
				isDuplicate: false,
				isValidDomain: true,
				isOld: true,
				button: `${1}`
			};

			domainObjectList.push(domainObject);
		});

		return domainObjectList;
	};

	// Populate existing users and domain of entity
	useEffect(() => {
		dispatch(clearErrors());

		const domainsFromAllEntities = [];
		const entityDomains = [];

		project?.orgStructure?.forEach((node) => {
			if (node.domains?.length > 0) {
				// Get all domains. May be duplicates.
				domainsFromAllEntities.push(...node.domains);

				// Get domains for current entity only. For rendering purposes
				if (node.entityId === entity.entityId) {
					entityDomains.push(...node.domains);
					return;
				}
			}
		});

		// This removes duplicates domains. This is to render existing domain options.
		const uniqueDomains = [...new Set(domainsFromAllEntities)];
		const projectDomainsDifference = uniqueDomains.filter((dom) => {
			if (!entityDomains.includes(dom)) {
				return dom;
			}
		});

		if (entityDomains.length > 0) {
			let sortedEntityDomains = [...entityDomains].sort();
			let renderedDomainList = buildDomainObjectList(sortedEntityDomains);

			setRenderedDomains(renderedDomainList);
			setDomainCount(renderedDomainList.length);
		}

		if (projectDomainsDifference.length > 0) {
			let sortedProjectDomains = [...projectDomainsDifference].sort();
			let availableProjectDomains = buildDomainObjectList(sortedProjectDomains, true);

			setProjectDomains(availableProjectDomains);
		}
	}, []);

	// Add domain row
	const addDomainbuttonClick = (val) => {
		const count = domainCount + 1;

		let newDomain = {
			id: `${DOMAIN_ID}${count}`,
			label: '',
			isEmpty: true,
			isValidDomain: true,
			isDuplicate: false,
			button: `${val}`
		};

		setRenderedDomains([...renderedDomains, newDomain]);

		setDomainError(false);
		setDomainCount(count);
	};

	// Remove domain row
	const removeDomainRow = (key, domainName) => {
		// If removed item was duplicate, mark other duplicate domain as valid domain
		let remainingDomains = renderedDomains
			.filter((domain) => domain.id !== key)
			.map((domain) => {
				if (domainName !== '' && domain.label === domainName && domain.id !== key) {
					const unDuplicateDomain = {...domain, isDuplicate: false, isValidDomain: true};

					return unDuplicateDomain;
				}

				return domain;
			});

		setRenderedDomains([...remainingDomains]);
		setDomainError(false);
	};

	// Update domain name. This will be called only for new domain being added
	const updateDomainName = (id, value) => {
		setRenderedDomains(
			renderedDomains.map((domain) => {
				if (domain.id === id) {
					return {...domain, label: value, isEmpty: false};
				}
				return domain;
			})
		);

		if (domainError && value) {
			setDomainError(false);
		}
	};

	// Set flag if domain name is valid or invalid
	const updateDomainValidation = (id, isValidDomain) => {
		setRenderedDomains(
			renderedDomains.map((domain) => {
				if (domain.id === id) {
					return {...domain, isValidDomain};
				}
				return domain;
			})
		);
	};

	// Set isEmpty property for domain
	const setEmptyDomain = (id, value) => {
		setRenderedDomains(
			renderedDomains.map((domain) => {
				if (domain.id === id) {
					return {...domain, isEmpty: value};
				}
				return domain;
			})
		);

		if (domainError && !value) {
			setDomainError(false);
		}
	};

	// If newly added domain is duplicate
	const setDomainDuplicate = (id, label, isDuplicate) => {
		if (isDuplicate === undefined) {
			return;
		}

		setRenderedDomains(
			renderedDomains.map((dom) => {
				if (dom.id === id) {
					return {...dom, isDuplicate, isEmpty: false};
				}

				if (dom.id !== id && dom.label === label) {
					return {...dom, isDuplicate, isEmpty: false};
				}

				return dom;
			})
		);

		setDomainError(isDuplicate);
	};

	// Before adding any new domain, ensure existing domains are valid
	const domainValidator = () => {
		let isValidData = true;

		if (renderedDomains.length > 0) {
			// If user provided duplicate name for any of the domains, set error flag to true
			// If any empty domain is added or domain name is not valid, set error flag to true
			const invalidDomains = renderedDomains.some((el) => el.isEmpty === true || !el.isValidDomain || el.isDuplicate);

			if (invalidDomains) {
				setDomainError(true);
				isValidData = false;
			}
		}

		return isValidData;
	};

	const renderDomainsSection = () => {
		const headingProps = {
			className: 'domain-section-heading',
			size: 'L',
			weight: 'bold'
		};

		const instructionalTextProps = {
			className: 'domain-instructional-text',
			size: 'L',
			weight: 'light'
		};

		const addNewDomainButtonProps = {
			className: domainError && 'disabled',
			label: labels.addNewDomain,
			onClick: () => {
				domainValidator() && addDomainbuttonClick(1);
			}
		};

		const addExistingDomainButtonProps = {
			className: (domainError || projectDomains?.length === 0) && 'disabled',
			label: labels.addExistingDomain,
			onClick: () => {
				domainValidator() && projectDomains.length > 0 && addDomainbuttonClick(2);
			}
		};

		const commonDomainProps = {
			domainError,
			setEmptyDomain,
			setDomainDuplicate,
			updateDomainName,
			updateDomainValidation,
			remove: removeDomainRow
		};

		const renderAddNewDomain = (domain, index) => {
			const addNewDomainProps = {
				...commonDomainProps,
				domain,
				actualDomains,
				renderedDomains
			};

			return <AddNewDomain key={domain.id} isOld={domain.isOld} {...addNewDomainProps} />;
		};

		const renderAddExistingDomain = (domain, index) => {
			const addExistingDomainProps = {
				...commonDomainProps,
				index,
				domain,
				projectDomains,
				renderedDomains
			};

			return <AddExistingDomain key={domain.id} {...addExistingDomainProps} />;
		};

		return (
			<div className="domains-section">
				<div className="domains-section">
					<OutputText {...headingProps}>{labels.domains}</OutputText>
					<OutputText {...instructionalTextProps}>{labels.editEntityDomainInstruction}</OutputText>
				</div>
				<div className="domain-input-wrapper">
					{renderedDomains.map((domain, index) =>
						domain.button == 1 ? renderAddNewDomain(domain, index) : renderAddExistingDomain(domain, index)
					)}
				</div>
				<div className="add-domain-buttons">
					<AddElementButton {...addNewDomainButtonProps}></AddElementButton>
					<AddElementButton {...addExistingDomainButtonProps}></AddElementButton>
				</div>
			</div>
		);
	};
	// ** Entity domain functions - End **

	return (
		<Form {...formProps}>
			{({values, errors, handleChange, handleSubmit, handleReset, isFormValid, isValueValid}) => {
				const formProps = {
					values,
					errors,
					handleChange,
					handleSubmit,
					handleReset,
					isFormValid,
					isValueValid,
					labels
				};

				const modalProps = {
					show: showModal,
					headerText: labels.editEntity,
					labels: {
						modalConfirmBtnLabel: labels.save,
						modalCancelBtnLabel: labels.cancelLabel,
						modalCloseBtnTitletip: labels.closeTitleModal
					},
					modalsize: 'medium',
					isOkDisabled: isLoading,
					isCancelDisabled: isLoading,
					onClose: () => {
						handleReset();
						resetStates();
						hideModal();
					},
					onOkClick: () => {
						handleSubmit(() => {
							handleReset();
							resetStates();
							hideModal();
						});
					},
					className: 'no-propagation'
				};

				return (
					<Modal {...modalProps}>
						{showModal && (
							<StyledEditEntityForm className="customScrollbar">
								<ErrorSummary />
								{isLoading && <LoadingIndicator view="inlineView" />}
								{!isLoading && renderEntityNameSection(formProps)}
								{!isLoading && renderDomainsSection()}
							</StyledEditEntityForm>
						)}
					</Modal>
				);
			}}
		</Form>
	);
};

EditEntityForm.propTypes = {
	showModal: PropTypes.bool.isRequired,
	hideModal: PropTypes.func.isRequired,
	entity: PropTypes.object,
	project: PropTypes.object
};

EditEntityForm.defaultProps = {};

export default EditEntityForm;
